import { useSnackbars } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useUpdateWebhook } from '@/services/db/webhook/mutations';
import { useOptionalWebhooks, useWebhook } from '@/services/db/webhook/queries';
import type { Webhook } from '@/services/db/webhook/entity';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useReportingWebhooksIndexHref } from '../hooks';
import { ReportingWebhookForm } from './reporting-webhook-form';
import {
  describeWebhook,
  type ReportingWebhookFormInput,
  type ReportingWebhookFormValues,
} from './reporting-webhook-schema';
import type { ReportingWebhookFormApi } from './types';

/** Only the re-baseline is needed here; the form callback hands back the plain RHF instance. */
type FormResetter = Pick<ReportingWebhookFormApi, 'reset'>;

/** Copies `events` so editing the selection cannot mutate the cached record in place. */
function webhookToFormValues(webhook: Webhook): ReportingWebhookFormInput {
  return {
    name: webhook.name,
    endpoint_url: webhook.endpoint_url,
    events: [...webhook.events],
    resolution: webhook.resolution,
    with_content: webhook.with_content,
    enabled: webhook.enabled,
  };
}

type EditReportingWebhookProps = {
  webhookId: string;
  /** Resolved by the page. Never read from the URL here — this renders inside a Suspense boundary. */
  workspaceId: number;
};

/**
 * Edits an existing reporting webhook. The full list is read alongside the record because two of
 * the form's signals need the workspace, not just this row: whether another webhook already claims
 * the endpoint, and whether switching this one off would leave none enabled.
 */
export function EditReportingWebhook({ webhookId, workspaceId }: EditReportingWebhookProps) {
  const { hasPermission } = usePermissions();
  const { data: webhook } = useWebhook({ id: webhookId, workspaceId });
  const { webhooks } = useOptionalWebhooks({ workspaceId });
  const snackbars = useSnackbars();
  const navigate = useSafeNavigate();
  const indexHref = useReportingWebhooksIndexHref(String(workspaceId));
  const updateWebhook = useUpdateWebhook();

  const otherWebhooks = webhooks.filter((candidate) => candidate.id !== webhook.id);

  /**
   * The one save both entry points run. `shouldRedirect` is the only difference: the footer button
   * sends the user to the list afterwards, while the leave guard must not — it is already holding
   * the user's real destination, and `safeNavigate` keeps a single pending-transition subscription,
   * so a second navigation here would replace theirs and land them on the list instead.
   */
  const save = async (
    values: ReportingWebhookFormValues,
    form: FormResetter,
    { shouldRedirect }: { shouldRedirect: boolean },
  ) => {
    try {
      await updateWebhook.mutateAsync({ webhookId, ...values });
      // Re-baseline before navigating: both leave-guards read `isDirty`, and the blocker is
      // evaluated when the router actually navigates — by which point `isSaving` has settled back
      // to false, so a still-dirty form would prompt about unsaved changes we just saved.
      form.reset(values, { keepDefaultValues: false });
      snackbars.create({
        variant: 'default',
        id: 'integrations.reporting-webhooks.edit.success',
        message: `${describeWebhook(values.name)} updated.`,
      });
      if (shouldRedirect) {
        navigate(indexHref);
      }
    } catch {
      snackbars.create({
        variant: 'error',
        id: 'integrations.reporting-webhooks.edit.error',
        message: "Your webhook couldn't be updated. Check your connection and try again.",
      });
    }
  };

  const onSubmit = (values: ReportingWebhookFormValues, form: FormResetter) =>
    save(values, form, { shouldRedirect: true });

  const onSaveAndStay = (values: ReportingWebhookFormValues, form: FormResetter) =>
    save(values, form, { shouldRedirect: false });

  return (
    <ReportingWebhookForm
      key={webhook.id}
      onSubmit={onSubmit}
      onSaveAndStay={onSaveAndStay}
      defaultValues={webhookToFormValues(webhook)}
      signingKey={webhook.signing_key}
      webhookId={webhook.id}
      otherEndpointUrls={otherWebhooks.map((candidate) => candidate.endpoint_url)}
      isLastEnabledWebhook={!otherWebhooks.some((candidate) => candidate.enabled)}
      readOnly={hasPermission('journeys/reporting_webhooks/edit') !== true}
      isSaving={updateWebhook.isPending}
      cancelHref={indexHref}
    />
  );
}
